// Rendering a calendar date in HER timezone — issue #1151.
//
// Pure and Deno-free so vitest can pin it (same split as smsCapture.ts,
// morningTaskPicture.ts, dueTodoBriefing.ts).
//
// -----------------------------------------------------------------------------
// THE BUG THIS EXISTS FOR
// -----------------------------------------------------------------------------
// `brain-dump-parse` prepended "Today is {date}." to the model's input so it
// could resolve "tomorrow", "friday", "tonight". It built that string with
//
//     new Date().toLocaleDateString("en-US", { weekday, year, month, day })
//
// and no `timeZone` option, so it rendered in the RUNTIME's zone. Supabase
// Edge Functions run in UTC, and UTC midnight is 8:00pm Eastern — so from
// early evening onward the parser was told it was already tomorrow, and every
// relative date it resolved landed a day late:
//
//   11pm Thursday Aug 27 ET  ->  prompt said "Friday, August 28"
//   "call the dentist tomorrow"  ->  Saturday, not Friday
//   "trash day is friday"        ->  the following Friday, a week out
//
// That last one is the worst: the prompt's rule is "the next upcoming
// Friday", and a model that already believes today is Friday skips a week.
//
// ⚠️ INVISIBLE ON A DEVELOPER MACHINE. A laptop set to Eastern renders the
// correct date with the buggy code, so this reproduces only where it runs.
// That is why the tests below pass an explicit zone rather than relying on
// the ambient one, and why `npm run test:tz` is meaningful here.
//
// The instant was never wrong — only the zone it was rendered in. So the fix
// is one option, not new clock plumbing.
//
// -----------------------------------------------------------------------------
// ⚠️ THREE COPIES OF "WHAT ZONE IS SHE IN" NOW EXIST
// -----------------------------------------------------------------------------
// This module, `kin-sms/smsCapture.ts` (localHourIn, for the 8pm-6am capture
// window) and `run-scheduled-tasks/scheduleTime.ts` (localPartsOf). They
// answer different questions and none is a copy-paste of another, but they
// share ONE fact — the fallback zone — and three hand-synced copies of one
// rule is this repo's most common defect shape (#361, #470, #515).
//
// So the VALUE is centralised here even though the functions are not.
// Consolidating the rest is a follow-up, not this fix.

/**
 * Where a user lives when we do not know.
 *
 * Matches `user_cap_day()` (migration 20260725120000), which made the same
 * choice for the free-tier day boundary, and `smsCapture.ts`'s
 * CAPTURE_FALLBACK_TIMEZONE. If these three ever disagree, one part of Kin
 * thinks it is a different day than another part — which is exactly the
 * class of bug CLAUDE.md documents twice over (the DAU redefinition, and
 * message_counts before #344).
 */
export const DEFAULT_USER_TIMEZONE = "America/New_York";

/** Whether `tz` is a non-empty string the runtime accepts as an IANA zone. */
export function isUsableTimeZone(tz: string | null | undefined): boolean {
  if (typeof tz !== "string" || !tz.trim()) return false;
  try {
    // Throws RangeError on an unknown zone. Cheap enough to do per request.
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

/**
 * First usable zone among `candidates`, else DEFAULT_USER_TIMEZONE.
 *
 * Takes a list rather than one value because callers genuinely have several
 * sources of varying quality — `profiles.timezone` (device-captured, the
 * source of truth per 20260725120000) then
 * `user_preferences.notifications_timezone` (mostly NULL, a notifications
 * artifact). Ordering them at the call site keeps that judgement visible
 * instead of buried in a chain of `??`.
 *
 * NEVER returns null. A caller that cannot resolve her zone must still
 * produce a date — refusing would break the parse entirely, which is far
 * worse than being an hour-band off for a user whose profile is empty.
 */
export function resolveUserTimeZone(
  ...candidates: Array<string | null | undefined>
): string {
  for (const tz of candidates) {
    if (isUsableTimeZone(tz)) return tz as string;
  }
  return DEFAULT_USER_TIMEZONE;
}

/**
 * "YYYY-MM-DD" on HER wall clock — moved here from
 * `kin-chat/createEventBuilder.ts` in #1780, unchanged.
 *
 * ⚠️ MOVED, NOT COPIED. kin-sms needs it to resolve her local date for a
 * confirmed series delete, and it cannot import from kin-chat. A second
 * definition is this repo's most common defect shape (see this file's own
 * header), and "what day is it for her" is precisely the fact that must not
 * fork: create_event and delete_event disagreeing by a day means
 * "stop it from today on" clears yesterday or misses today.
 * `createEventBuilder.ts` re-exports this so every existing importer keeps
 * its path.
 *
 * `en-CA` is the locale whose short date format IS ISO 8601.
 */
export function todayYmdInZone(instant: Date, timeZone: string): string {
  try {
    return instant.toLocaleDateString("en-CA", { timeZone });
  } catch {
    return instant.toLocaleDateString("en-CA", { timeZone: DEFAULT_USER_TIMEZONE });
  }
}

/**
 * "Thursday, August 27, 2026" — as it reads on HER wall clock.
 *
 * Byte-identical in shape to what `toLocaleDateString("en-US", { weekday:
 * "long", year: "numeric", month: "long", day: "numeric" })` produced, so
 * the prompt string the model has been trained against does not change —
 * only which day it names.
 *
 * An unusable zone falls back rather than throwing: a prompt with a slightly
 * wrong date still parses her dump; a 500 loses it.
 */
export function formatLongDateInZone(
  instant: Date,
  timeZone: string | null | undefined,
): string {
  const zone = resolveUserTimeZone(timeZone);
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(instant);
  } catch {
    // resolveUserTimeZone already validated, so reaching here means the
    // INSTANT is bad (an Invalid Date). Nothing sensible to render.
    return new Intl.DateTimeFormat("en-US", {
      timeZone: DEFAULT_USER_TIMEZONE,
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date());
  }
}

// -----------------------------------------------------------------------------
// EXACT LOCAL-DAY BOUNDS — issue #1664
// -----------------------------------------------------------------------------
// These arrived in dispatch-notifications/prepCalendarCandidates.ts for #1663's
// 3-day prep window and moved here unchanged so the morning note's day fetch
// can use the same arithmetic. Two modules needing "when does her day start"
// is exactly the three-hand-synced-copies shape the header above warns about.
//
// What they replace: a fixed 30-hour band, 12:00Z ± 15h, whose comment claimed
// it "always covers midnight to midnight of localDate in any IANA timezone
// (UTC-12 to UTC+14)". It does not, and the gap is a whole part of her day:
//
//   UTC+14  local day D runs from D-1 10:00Z; the band started D-1 21:00Z,
//           so 00:00–11:00 on her wall clock — her entire MORNING — was
//           outside the window the morning note fetched.
//   UTC-12  local day D ends D+1 12:00Z; the band ended D+1 03:00Z, so
//           15:00–24:00 local — her EVENING — was outside it.
//
// Nothing errored. A mom in Auckland got a morning note that had quietly
// dropped everything before 11am, which is the part of the day a morning note
// exists to describe.

/** Wall-clock parts of `instant` as read in `timeZone`. h is 0-23. */
function partsInZone(
  instant: Date,
  timeZone: string,
): { y: number; m: number; d: number; h: number; min: number; s: number } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const get = (type: string) =>
    parseInt(fmt.formatToParts(instant).find((p) => p.type === type)?.value ?? "0", 10);
  return { y: get("year"), m: get("month"), d: get("day"), h: get("hour") % 24, min: get("minute"), s: get("second") };
}

/**
 * The UTC instant of LOCAL midnight on `ymd` in `timeZone`.
 *
 * Exact for any IANA zone including DST transition days — two Intl round-trips
 * converge, because the first correction lands within an hour of the answer and
 * the second resolves the offset that actually applies there.
 */
export function zonedMidnightUtc(ymd: string, timeZone: string): Date {
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const wantedAsUtc = Date.UTC(y, m - 1, d, 0, 0, 0);
  let guess = wantedAsUtc;
  for (let i = 0; i < 2; i++) {
    const parts = partsInZone(new Date(guess), timeZone);
    const seenAsUtc = Date.UTC(parts.y, parts.m - 1, parts.d, parts.h, parts.min, parts.s);
    guess = guess - (seenAsUtc - wantedAsUtc);
  }
  return new Date(guess);
}

/**
 * The UTC instant of `hour`:00 on `ymd`, on HER wall clock in `timeZone`.
 * Same two-pass convergence as zonedMidnightUtc, so it stays exact on DST
 * transition days (Nov 1 2026: midnight + 8h would have read 7 AM).
 */
export function wallClockUtc(ymd: string, hour: number, timeZone: string): Date {
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const wantedAsUtc = Date.UTC(y, m - 1, d, hour, 0, 0);
  let guess = wantedAsUtc;
  for (let i = 0; i < 2; i++) {
    const parts = partsInZone(new Date(guess), timeZone);
    const seenAsUtc = Date.UTC(parts.y, parts.m - 1, parts.d, parts.h, parts.min, parts.s);
    guess = guess - (seenAsUtc - wantedAsUtc);
  }
  return new Date(guess);
}

/** `ymd` shifted by `days`, still YYYY-MM-DD. Noon UTC avoids the DST edges
 *  that a midnight basis would hit while doing pure date arithmetic. */
export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d + days, 12));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

/**
 * [start, end) as ISO instants covering every local moment of the days
 * `startYmd`..`endYmd` inclusive, in her zone.
 *
 * `end` is local midnight of the day AFTER endYmd, so the range is
 * half-open — pass it to a `gte`/`lt` pair, or to a calendar API's
 * timeMin/timeMax, and no local moment of her day is outside it.
 */
export function localRangeUtc(
  startYmd: string,
  endYmd: string,
  timeZone: string,
): { start: string; end: string } {
  return {
    start: zonedMidnightUtc(startYmd, timeZone).toISOString(),
    end: zonedMidnightUtc(addDaysYmd(endYmd, 1), timeZone).toISOString(),
  };
}
